import { SkatingPassData } from '@types';
import { HelperMethods } from '_utils';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import React, { useRef } from 'react';
import Barcode from 'react-barcode';
import {
  MdCheckCircle,
  MdDownload,
  MdEvent,
  MdLocationOn,
  MdPhotoCamera,
  MdPrint,
} from 'react-icons/md';
import main_logo from '../../assets/logos/main_logo.png';
import { excludeFromTicketCapture, SuccessAppDownloadBanner } from '_atoms';

interface PassConfirmationProps {
  passData: SkatingPassData;
  passNumber: string;
}

export const PassConfirmation: React.FC<PassConfirmationProps> = ({
  passData,
  passNumber,
}) => {
  const ticketElementRef = useRef<HTMLDivElement>(null);

  if (!passData.selectedPass) {
    return null;
  }

  const pass = passData.selectedPass;

  const generateTicketPDF = async (): Promise<{
    pdf: jsPDF;
    filename: string;
  }> => {
    if (!ticketElementRef.current) {
      throw new Error('Ticket element not found');
    }

    // Convert the ticket element to an image
    const png = await toPng(ticketElementRef.current, {
      quality: 1.0,
      backgroundColor: '#ffffff',
      filter: excludeFromTicketCapture,
    });

    // Create a new jsPDF instance
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Create an image element to get dimensions
    const img = new Image();
    img.src = png;

    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Calculate dimensions to fit the image on the page
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = (img.height * imgWidth) / img.width;

          // If image is too tall, scale it down
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;

          if (imgHeight > pageHeight - margin * 2) {
            finalHeight = pageHeight - margin * 2;
            finalWidth = (img.width * finalHeight) / img.height;
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(png, 'PNG', x, y, finalWidth, finalHeight);

          // Generate filename using pass name
          const filename = `MOI_Pass_${pass.name.replace(/ /g, '_')}.pdf`;

          resolve({ pdf, filename });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load ticket image'));
      };
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const { pdf, filename } = await generateTicketPDF();
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handlePrint = async () => {
    try {
      const { pdf } = await generateTicketPDF();
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.print();
      }
    } catch (error) {
      console.error('Error printing:', error);
    }
  };

  const handleSavePhoto = async () => {
    try {
      if (!ticketElementRef.current) {
        throw new Error('Ticket element not found');
      }

      // Convert the ticket element to a PNG image
      const dataUrl = await toPng(ticketElementRef.current, {
        quality: 1.0,
        backgroundColor: '#ffffff',
        pixelRatio: 2, // Higher resolution for better quality
        filter: excludeFromTicketCapture,
      });

      // Create a download link
      const link = document.createElement('a');
      link.download = `MOI_Pass_${pass.name.replace(/ /g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error saving photo:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Ticket Content */}
      <div ref={ticketElementRef} className="space-y-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={main_logo} alt="MOI Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold">
                  Pass Booking Confirmation
                </h1>
                <p className="text-blue-100">
                  Your skating pass has been successfully booked!
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <MdCheckCircle className="text-green-400 w-6 h-6" />
                <span className="font-semibold">Confirmed</span>
              </div>
              <p className="text-sm text-blue-100">
                {HelperMethods.formatDateTimeLocal(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Pass Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <MdEvent className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Pass Information</p>
                  <p className="font-medium text-gray-900">{pass.name}</p>
                  {/* <p className="text-sm text-gray-600">Type: {pass.type}</p> */}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdEvent className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Max Uses</p>
                  <p className="font-medium text-gray-900">
                    {pass.maxUses === null || pass.maxUses === 0
                      ? 'Unlimited'
                      : `${pass.maxUses} sessions`}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdLocationOn className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <a
                    href="https://www.google.com/maps/dir/Modesto+on+Ice,+11th+St,+Modesto,+CA+95354"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    1131 11th Street • Modesto, CA 95354
                  </a>
                </div>
              </div>
            </div>

            {/* Barcode */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Barcode
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center flex items-center justify-center">
                    <Barcode value={passNumber} format="CODE128" />
                  </div>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    {passNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          {pass.disclaimer && (
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">{pass.disclaimer}</p>
              </div>
            </div>
          )}

          {/* Total Amount */}
          {(passData.selectedPrice ||
            (passData.selectedPassType === 'adult'
              ? pass.adultPrice
              : pass.childPrice)) > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-blue-50 p-4 rounded">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Amount:
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    $
                    {(
                      passData.selectedPrice ||
                      (passData.selectedPassType === 'adult'
                        ? pass.adultPrice
                        : pass.childPrice)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <SuccessAppDownloadBanner />

          {/* Important Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Important Information
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• You will receive a confirmation email of your booking</li>
                <li>
                  • For ticket purchases, you must be logged into your Modesto
                  On Ice (MOI) account when making the purchase for your pass to
                  apply.
                </li>
                <li>
                  • All online purchases must be completed at least 30 minutes
                  before the skating session begins.
                </li>
                <li>• Keep this confirmation for your records</li>
                <li>
                  • Please make sure all skating guests complete their online
                  waiver at{' '}
                  <a
                    href="https://tickets.modestoonice.com/waivers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    https://tickets.modestoonice.com/waivers
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={handlePrint}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdPrint className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            onClick={handleSavePhoto}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <MdPhotoCamera className="w-4 h-4 mr-2" />
            Save Photo
          </button>
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdDownload className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};
